import React, { useEffect, useState } from 'react';
import { Container, Fab, Tooltip, Paper } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import Greetings from './Greetings';
import BirthdayWidget from './BirthdayWidget';
import WorkAnniversaryWidget from './WorkAnniversaryWidget';
import MarketHolidaysWidget from '../../components/dashboard/MarketHolidaysWidget';
import { useAllEmployeeDetails, useAppliedCandidates } from '../../hooks/useCandidate';
import { axiosInstance } from '../../api';
import { useNavigate } from 'react-router';

// ---- FORTHEYE soft-card tokens (shared with LeaveTracker / TeamLeaves) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

function DashboardComponent() {
  const navigation = useNavigate();
  const { data: emp = [] } = useAllEmployeeDetails();
  const { data: applicantsData = [] } = useAppliedCandidates();
  const [attendanceStats, setAttendanceStats] = useState({
    present_count: 0,
    absent_count: 0,
    on_duty_count: 0,
    comp_off_count: 0,
    total_strength: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Local calendar date (not UTC) so the requested day matches the user's.
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        // fallback=latest: if today has no attendance yet (weekend / pre-upload),
        // show the most recent recorded day instead of a misleading 0.
        const response = await axiosInstance.get(`/api/attendance/daily-status/?date=${today}&fallback=latest`);
        setAttendanceStats({
          present_count: response.data.present_count,
          absent_count: response.data.absent_count,
          on_duty_count: response.data.on_duty_count,
          comp_off_count: response.data.comp_off_count,
          total_strength: response.data.total_strength,
          date: response.data.date,
          is_fallback: response.data.is_fallback,
        });
      } catch (error) {
        console.error("Error fetching dashboard attendance stats:", error);
      }
    };
    fetchStats();
  }, []);

  const handleNavigation = (val, filter) => {
    if (val === 'Resumes') {
      navigation('/totalcandidates');
    } else if (val === 'employees') {
      navigation('/employees');
    } else if (val === 'whatsapp') {
      navigation('/whatsapp-dashboard');
    } else if (val === 'attendance') {
      navigation(`/attendance/present${filter ? `?filter=${filter}` : ''}`);
    }
  };

  const statsCards = [
    {
      icon: <PersonIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />,
      title: 'Employees',
      value: attendanceStats.total_strength || emp?.length || 0,
      color: '#7c5cff',
      onClick: () => handleNavigation('employees'),
    },
    {
      icon: <HowToRegIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />,
      title: 'Present',
      value: attendanceStats.present_count,
      color: ACCENT,
      onClick: () => handleNavigation('attendance', 'present'),
    },
    {
      icon: <FileDownloadDoneIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />,
      title: 'Resumes',
      value: applicantsData.length,
      color: '#16a34a',
      onClick: () => handleNavigation('Resumes'),
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        position: 'relative',
        display: 'flex',
      }}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 2, lg: 3 },
          flex: 1,
        }}
      >
        {/* Middle Main Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Greetings Section */}
          <Paper
            elevation={0}
            sx={{
              ...CARD_SX,
              mb: { xs: 2, md: 3 },
            }}
          >
            <Greetings />
          </Paper>

          {/* Stats Cards Section - Compressed into 2 cols */}
          <Box
            sx={{
              mb: { xs: 2, md: 3 },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              {statsCards.map((card, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  onClick={card.onClick}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    p: { xs: 2, sm: 2.5 },
                    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
                    border: '1px solid #eef0f4',
                    cursor: card.onClick ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: card.onClick ? 'translateY(-3px)' : 'none',
                      boxShadow: '0 12px 28px rgba(17,24,39,0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: `${card.color}14`,
                        color: card.color,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: '#6b7280',
                          fontWeight: 600,
                          fontSize: '0.62rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          mb: 0.25,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        sx={{
                          lineHeight: 1.1,
                          color: card.color,
                          fontSize: '1.7rem',
                          fontWeight: 800,
                        }}
                      >
                        {card.value}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>

          {/* Market Holidays Section */}
          <Box
            sx={{
              mb: { xs: 2, md: 3 },
            }}
          >
            <MarketHolidaysWidget />
          </Box>
        </Box>

        {/* Right Secondary Sidebar */}
        <Box
          sx={{
            width: { xs: '100%', lg: '350px' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, md: 3 },
          }}
        >
          <BirthdayWidget />
          <WorkAnniversaryWidget />
        </Box>
      </Container>

      {/* Floating WhatsApp Button — stacked above the global Support Inbox
          launcher (PrivateSection.js), which sits at bottom:16/24 on every
          admin page, so the two don't overlap on /dashboard. */}
      < Tooltip title="WhatsApp Business" placement="left" >
        <Fab
          onClick={() => handleNavigation('whatsapp')}
          sx={{
            position: 'fixed',
            bottom: { xs: 84, md: 96 },
            right: { xs: 16, md: 24 },
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            color: '#fff',
            width: { xs: 56, md: 64 },
            height: { xs: 56, md: 64 },
            boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'pulse 2s infinite',
            zIndex: 1000,
            '&:hover': {
              background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
              transform: 'scale(1.1)',
              boxShadow: '0 12px 32px rgba(37, 211, 102, 0.5)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <WhatsAppIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
        </Fab>
      </Tooltip >

      {/* CSS Animations */}
      < style >
        {`
          @keyframes fadeIn {
            from { 
              opacity: 0; 
            }
            to { 
              opacity: 1; 
            }
          }
          
          @keyframes fadeInUp {
            from { 
              opacity: 0; 
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideInUp {
            from { 
              opacity: 0; 
              transform: translateY(40px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% { 
              transform: scale(1); 
            }
            50% { 
              transform: scale(1.05); 
            }
          }
        `}
      </style>
    </Box>
  );
}

export default DashboardComponent;